import { useEffect, useState } from "react";
import {
  AlertCircle,
  Check,
  Download,
  FileSearch,
  FileX2,
  Loader2,
  Pencil,
  X,
} from "lucide-react";
import {
  BankingContractPayload,
  BankingContractPeriodPayload,
  editBankingContract,
  getBankingContractById,
} from "../../../../Rest_api/RestApi";
import {
  BankingContractDetail,
  BankingContractPeriodResponse,
} from "../../../../models/banking";
import { TraderSelect } from "../components/TraderSelect";
import {
  PdfDownloadPopup,
  PdfPopupState,
  downloadBankingContractPdf,
} from "../components/PdfDownloadPopup";

function fmt(val: string | number | null | undefined, suffix = ""): string {
  if (val === null || val === undefined || val === "") return "--";
  return `${val}${suffix}`;
}

function fmtDate(val: string | null | undefined): string {
  if (!val) return "--";
  const [y, m, d] = val.split("-");
  if (!y || !m || !d) return val;
  return `${d}-${m}-${y}`;
}

const inputClass =
  "w-full border border-[#E2E2E5] bg-white rounded-md h-8 text-[12px] px-2 outline-none text-[#1A1B2B] placeholder:text-[#A8A6B2] focus:border-[#F1935C]";

const parseNum = (s: string): number | null => {
  const t = (s ?? "").toString().trim();
  if (!t) return null;
  const n = Number(t.replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? n : null;
};

// Editable drafts (string-valued for inputs); basis_raw + return quantum are preserved/derived, not edited.
type DraftSupply = {
  start: string;
  end: string;
  quantum: string;
  quantumIsMax: boolean;
  from_time: string;
  to_time: string;
  basis: string;
  basis_raw: string;
};

type DraftReturn = {
  start: string;
  end: string;
  bankingFactor: string;
  quantum: string; // blank = auto-derive from banking factor × supply
  quantumIsMax: boolean; // preserved
  from_time: string;
  to_time: string;
  basis: string;
  basis_raw: string;
};

type Draft = {
  loa_number: string;
  loa_date: string;
  supplier: string;
  receiver: string;
  traderName: string;
  notes: string;
  supply: DraftSupply[];
  return: DraftReturn[];
};

function toDraft(c: BankingContractDetail): Draft {
  return {
    loa_number: c.loa_number ?? "",
    loa_date: c.loa_date ?? "",
    supplier: c.supplier ?? "",
    receiver: c.receiver ?? "",
    traderName: c.traderName ?? c.trader ?? "",
    notes: c.notes ?? "",
    supply: c.supply_periods.map((p) => ({
      start: p.start ?? "",
      end: p.end ?? "",
      quantum: p.quantum_mw == null ? "" : `${p.quantum_mw}`,
      quantumIsMax: !!p.quantum_is_max,
      from_time: p.from_time ?? "",
      to_time: p.to_time ?? "",
      basis: p.basis ?? "firm",
      basis_raw: p.basis_raw ?? "",
    })),
    return: c.return_periods.map((p) => ({
      start: p.start ?? "",
      end: p.end ?? "",
      bankingFactor:
        p.banking_factor_pct == null ? "" : `${p.banking_factor_pct}`,
      quantum: p.quantum_mw == null ? "" : `${p.quantum_mw}`,
      quantumIsMax: !!p.quantum_is_max,
      from_time: p.from_time ?? "",
      to_time: p.to_time ?? "",
      basis: p.basis ?? "firm",
      basis_raw: p.basis_raw ?? "",
    })),
  };
}

function draftToPayload(d: Draft): BankingContractPayload {
  const supply_periods: BankingContractPeriodPayload[] = d.supply.map((p) => ({
    start: p.start,
    end: p.end,
    quantum_mw: parseNum(p.quantum),
    quantum_is_max: p.quantumIsMax,
    from_time: p.from_time,
    to_time: p.to_time,
    basis: p.basis,
    basis_raw: p.basis_raw,
  }));

  const return_periods: BankingContractPeriodPayload[] = d.return.map((p) => {
    const row: BankingContractPeriodPayload = {
      start: p.start,
      end: p.end,
      quantum_mw: parseNum(p.quantum), // blank → null, so backend re-derives from banking factor × supply
      quantum_is_max: p.quantumIsMax,
      from_time: p.from_time,
      to_time: p.to_time,
      basis: p.basis,
      basis_raw: p.basis_raw,
    };
    const bf = parseNum(p.bankingFactor);
    if (bf !== null) row.banking_factor_pct = bf;
    return row;
  });

  return {
    loa_number: d.loa_number.trim(),
    loa_date: d.loa_date,
    supplier: d.supplier.trim(),
    receiver: d.receiver.trim(),
    notes: d.notes,
    trader: d.traderName,
    supply_periods,
    return_periods,
  };
}

function Badge({ count }: { count: number }) {
  return (
    <span className="ml-2 px-2 py-0.5 rounded-full bg-[#F2F2F5] text-[#6B6877] text-[11px] font-medium">
      {count}
    </span>
  );
}

function FieldCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] text-[#8C8A96]">{label}</span>
      <span className="text-[12px] font-medium text-[#1A1B2B]">{value}</span>
    </div>
  );
}

/**
 * Read-only Trader Name cell: renders each (comma-joined) trader as a filled
 * chip so multiple selected traders read as distinct tags rather than one long
 * wrapping string. Falls back to a dash when nothing is selected. Chip palette
 * matches the multi-select dropdown in TraderSelect.
 */
function TraderChipsCell({ label, value }: { label: string; value: string }) {
  const traders = value
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] text-[#8C8A96]">{label}</span>
      {traders.length === 0 ? (
        <span className="text-[12px] font-medium text-[#1A1B2B]">—</span>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5">
          {traders.map((t) => (
            <span
              key={t}
              className="inline-flex items-center px-2.5 py-[3px] rounded-full bg-[#FCEBDC] text-[#C2652F] text-[11px] font-medium leading-none"
            >
              {t}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function EditCell({
  label,
  children,
  error,
  className = "",
}: {
  label: string;
  children: React.ReactNode;
  error?: boolean;
  className?: string;
}) {
  return (
    <label className={`flex flex-col gap-1 min-w-0 ${className}`}>
      <span
        className={`text-[11px] ${error ? "text-[#C8311C]" : "text-[#8C8A96]"}`}
      >
        {label}
        {error ? " *" : ""}
      </span>
      {children}
    </label>
  );
}

function PeriodRow({
  period,
  isReturn,
}: {
  period: BankingContractPeriodResponse;
  isReturn?: boolean;
}) {
  const isAsAndWhen = period.basis?.toLowerCase() === "as_and_when";
  const quantumValue = (() => {
    const formatted = fmt(period.quantum_mw, " MW");
    return isAsAndWhen && formatted !== "--" ? `Upto ${formatted}` : formatted;
  })();

  const cols = isReturn
    ? [
        { label: "Start Date", value: fmtDate(period.start) },
        { label: "End Date", value: fmtDate(period.end) },
        { label: "Quantum", value: quantumValue },
        { label: "Banking Factor", value: fmt(period.banking_factor_pct, "%") },
        { label: "Start Time", value: fmt(period.from_time) },
        { label: "End Time", value: fmt(period.to_time) },
        {
          label: "Basis",
          value: period.basis
            ? period.basis.charAt(0).toUpperCase() + period.basis.slice(1)
            : "--",
        },
      ]
    : [
        { label: "Start Date", value: fmtDate(period.start) },
        { label: "End Date", value: fmtDate(period.end) },
        { label: "Quantum", value: quantumValue },
        { label: "Max Quantum", value: period.quantum_is_max ? "Yes" : "No" },
        { label: "Start Time", value: fmt(period.from_time) },
        { label: "End Time", value: fmt(period.to_time) },
        {
          label: "Basis",
          value: period.basis
            ? period.basis.charAt(0).toUpperCase() + period.basis.slice(1)
            : "--",
        },
      ];

  return (
    <div className="grid grid-cols-7 gap-x-3 gap-y-1 py-3 border-b border-[#ECEEF3] last:border-b-0">
      {cols.map((col) => (
        <div key={col.label} className="flex flex-col gap-0.5">
          <span className="text-[11px] text-[#8C8A96]">{col.label}</span>
          <span className="text-[12px] font-medium text-[#1A1B2B]">
            {col.value}
          </span>
        </div>
      ))}
    </div>
  );
}

function BasisSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={inputClass}
    >
      <option value="firm">Firm</option>
      <option value="as_and_when">As &amp; When</option>
    </select>
  );
}

function EditSupplyRow({
  period,
  onChange,
}: {
  period: DraftSupply;
  onChange: <K extends keyof DraftSupply>(
    key: K,
    value: DraftSupply[K],
  ) => void;
}) {
  return (
    <div className="grid grid-cols-7 gap-x-3 gap-y-1 py-3 border-b border-[#ECEEF3] last:border-b-0">
      <EditCell label="Start Date">
        <input
          type="date"
          value={period.start}
          onChange={(e) => onChange("start", e.target.value)}
          className={inputClass}
        />
      </EditCell>
      <EditCell label="End Date">
        <input
          type="date"
          value={period.end}
          onChange={(e) => onChange("end", e.target.value)}
          className={inputClass}
        />
      </EditCell>
      <EditCell label="Quantum (MW)">
        <input
          value={period.quantum}
          onChange={(e) => onChange("quantum", e.target.value)}
          placeholder="E.g. 100"
          className={inputClass}
        />
      </EditCell>
      <EditCell label="Max Quantum">
        <select
          value={period.quantumIsMax ? "max" : "exact"}
          onChange={(e) => onChange("quantumIsMax", e.target.value === "max")}
          className={inputClass}
        >
          <option value="exact">No</option>
          <option value="max">Yes</option>
        </select>
      </EditCell>
      <EditCell label="Start Time">
        <input
          type="time"
          value={period.from_time}
          onChange={(e) => onChange("from_time", e.target.value)}
          className={inputClass}
        />
      </EditCell>
      <EditCell label="End Time">
        <input
          type="time"
          value={period.to_time}
          onChange={(e) => onChange("to_time", e.target.value)}
          className={inputClass}
        />
      </EditCell>
      <EditCell label="Basis">
        <BasisSelect
          value={period.basis}
          onChange={(v) => onChange("basis", v)}
        />
      </EditCell>
    </div>
  );
}

function EditReturnRow({
  period,
  onChange,
}: {
  period: DraftReturn;
  onChange: <K extends keyof DraftReturn>(
    key: K,
    value: DraftReturn[K],
  ) => void;
}) {
  return (
    <div className="grid grid-cols-7 gap-x-3 gap-y-1 py-3 border-b border-[#ECEEF3] last:border-b-0">
      <EditCell label="Start Date">
        <input
          type="date"
          value={period.start}
          onChange={(e) => onChange("start", e.target.value)}
          className={inputClass}
        />
      </EditCell>
      <EditCell label="End Date">
        <input
          type="date"
          value={period.end}
          onChange={(e) => onChange("end", e.target.value)}
          className={inputClass}
        />
      </EditCell>
      <EditCell label="Quantum (MW)">
        <input
          value={period.quantum}
          onChange={(e) => onChange("quantum", e.target.value)}
          placeholder="Auto"
          className={inputClass}
        />
      </EditCell>
      <EditCell label="Banking Factor (%)">
        <input
          value={period.bankingFactor}
          onChange={(e) => onChange("bankingFactor", e.target.value)}
          placeholder="E.g. 105"
          className={inputClass}
        />
      </EditCell>
      <EditCell label="Start Time">
        <input
          type="time"
          value={period.from_time}
          onChange={(e) => onChange("from_time", e.target.value)}
          className={inputClass}
        />
      </EditCell>
      <EditCell label="End Time">
        <input
          type="time"
          value={period.to_time}
          onChange={(e) => onChange("to_time", e.target.value)}
          className={inputClass}
        />
      </EditCell>
      <EditCell label="Basis">
        <BasisSelect
          value={period.basis}
          onChange={(v) => onChange("basis", v)}
        />
      </EditCell>
    </div>
  );
}

export function DocumentsTab({
  contractId,
  onContractUpdated,
}: {
  contractId: string;
  // Called after a successful edit so the parent can re-fetch the analytics
  // that feed the Overview chart.
  onContractUpdated?: () => void;
}) {
  const [contract, setContract] = useState<BankingContractDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [missing, setMissing] = useState<Record<string, boolean>>({});

  const [reloadNonce, setReloadNonce] = useState(0);

  const [downloading, setDownloading] = useState(false);
  const [pdfPopup, setPdfPopup] = useState<PdfPopupState | null>(null);

  const actualId = contractId.split("-").pop() || contractId;

  useEffect(() => {
    let cancelled = false;
    setContract(null);
    setError(null);
    setLoading(true);
    // Reset edit state when switching contracts.
    setIsEditing(false);
    setDraft(null);
    setSaveError(null);
    setMissing({});
    getBankingContractById(actualId)
      .then((data) => {
        if (cancelled) return;
        setContract(data);
        if (!data) setError("Contract not found.");
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load contract details.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [actualId, reloadNonce]);

  const startEdit = () => {
    if (!contract) return;
    setDraft(toDraft(contract));
    setSaveError(null);
    setMissing({});
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setDraft(null);
    setSaveError(null);
    setMissing({});
  };

  const handleDownloadPdf = async () => {
    if (downloading) return;
    setDownloading(true);
    setPdfPopup(null);
    try {
      setPdfPopup(await downloadBankingContractPdf(actualId));
    } finally {
      setDownloading(false);
    }
  };

  const setBasic = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => (d ? { ...d, [key]: value } : d));

  const updateSupply = <K extends keyof DraftSupply>(
    idx: number,
    key: K,
    value: DraftSupply[K],
  ) =>
    setDraft((d) =>
      d
        ? {
            ...d,
            supply: d.supply.map((p, i) =>
              i === idx ? { ...p, [key]: value } : p,
            ),
          }
        : d,
    );

  const updateReturn = <K extends keyof DraftReturn>(
    idx: number,
    key: K,
    value: DraftReturn[K],
  ) =>
    setDraft((d) =>
      d
        ? {
            ...d,
            return: d.return.map((p, i) =>
              i === idx ? { ...p, [key]: value } : p,
            ),
          }
        : d,
    );

  const extractBackendError = (res: any): string => {
    const data = res?.data;
    if (!data) return res?.message || "Failed to update contract";
    if (typeof data === "string") return data;
    return (
      data.error ||
      data.message ||
      data.detail ||
      (Array.isArray(data.errors) ? data.errors.join(", ") : null) ||
      res.message ||
      "Failed to update contract"
    );
  };

  const handleSave = async () => {
    if (!draft || saving) return;
    const miss: Record<string, boolean> = {
      loa_number: !draft.loa_number.trim(),
      loa_date: !draft.loa_date.trim(),
      supplier: !draft.supplier.trim(),
      receiver: !draft.receiver.trim(),
      traderName: !draft.traderName.trim(),
    };
    setMissing(miss);
    if (Object.values(miss).some(Boolean)) {
      setSaveError(
        "Please fill in all required fields (LoA Number, LoA Date, Supplier, Receiver, Trader Name).",
      );
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const res = await editBankingContract(actualId, draftToPayload(draft));
      if (res && res.success === false) {
        setSaveError(extractBackendError(res));
        return;
      }
      if (res && typeof res === "object" && "id" in res) {
        // The edit API returns the trader under `trader`; the rest of the app reads `traderName`.
        setContract({
          ...(res as BankingContractDetail),
          traderName: (res as any).traderName ?? (res as any).trader,
        });
      } else {
        // Fallback: re-fetch if the response wasn't the contract object.
        const fresh = await getBankingContractById(actualId);
        if (fresh) setContract(fresh);
      }
      setIsEditing(false);
      setDraft(null);
      setMissing({});
      // Edits change the supply/return schedule that drives the Overview chart;
      // notify the parent so it re-fetches the contract analytics.
      onContractUpdated?.();
    } catch (err: any) {
      setSaveError(
        typeof err === "string"
          ? err
          : err?.message || "Failed to update contract",
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-[#ECEEF3] rounded-[10px] px-4 py-8 flex items-center justify-center text-[13px] text-[#8C8A96] mb-3.5">
        Loading contract details...
      </div>
    );
  }

  if (error || !contract) {
    const isLoadError = error === "Failed to load contract details.";
    return (
      <div className="bg-white border border-[#ECEEF3] rounded-[10px] px-6 py-14 flex flex-col items-center justify-center text-center mt-3.5 mb-3.5">
        <div
          className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
            isLoadError ? "bg-[#FEF2F2]" : "bg-[#FFF7F0]"
          }`}
        >
          {isLoadError ? (
            <AlertCircle size={30} className="text-[#C8311C]" />
          ) : (
            <FileX2 size={30} className="text-[#E07B43]" />
          )}
        </div>
        <div className="text-[15px] font-semibold text-[#1A1B2B]">
          {isLoadError ? "Couldn't load contract" : "No contract on file"}
        </div>
        <p className="text-[12.5px] text-[#8C8A96] mt-1.5 max-w-[340px] leading-relaxed">
          {isLoadError
            ? "Something went wrong while fetching this contract. Check your connection and try again."
            : "This banking arrangement doesn't have a contract document attached yet. Once a contract is added, its details and schedule will appear here."}
        </p>
        {isLoadError ? (
          <button
            type="button"
            onClick={() => setReloadNonce((n) => n + 1)}
            className="mt-5 inline-flex items-center gap-1.5 border border-[#F1935C] bg-white rounded-md px-4 py-1.5 text-[12px] text-[#E07B43] font-semibold hover:bg-[#FFF7F0] cursor-pointer"
          >
            <Loader2 size={13} /> Retry
          </button>
        ) : (
          <div className="mt-5 inline-flex items-center gap-1.5 text-[11.5px] text-[#A8A6B2]">
            <FileSearch size={13} /> Add a contract to populate this view
          </div>
        )}
      </div>
    );
  }

  const capturedDate = contract.created_at
    ? fmtDate(contract.created_at.split("T")[0])
    : "--";
  const editing = isEditing && draft;

  return (
    <div className="flex flex-col gap-3 mb-3.5">
      {/* Contract Details header */}
      <div className="bg-white border border-[#ECEEF3] rounded-[10px] px-5 py-4 mt-3.5 flex items-start justify-between gap-3">
        <div>
          <div className="text-[14px] font-semibold text-[#1A1B2B]">
            Contract Details
          </div>
          <div className="text-[11px] text-[#8C8A96] mt-0.5">
            {editing
              ? "Editing contract — changes replace the saved schedule on save."
              : `Captured on ${capturedDate} · Read-only snapshot of the form submission`}
          </div>
        </div>
        {editing ? (
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={cancelEdit}
              disabled={saving}
              className="inline-flex items-center gap-1.5 border border-[#ECEEF3] bg-white rounded-md px-3 py-1.5 text-[12px] text-[#6B6877] font-medium hover:bg-[#FAFBFD] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <X size={13} /> Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 border border-[#F1935C] bg-[#F1935C] rounded-md px-4 py-1.5 text-[12px] text-white font-semibold hover:bg-[#E07B43] hover:border-[#E07B43] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Check size={13} />
              )}
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={downloading}
              title={contract.pdf_filename || "Download contract PDF"}
              className="inline-flex items-center gap-1.5 border border-[#F1935C] bg-white rounded-full px-4 py-1.5 text-[12px] text-[#E07B43] font-semibold bg-[#fffaf6] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {downloading ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Download size={13} />
              )}
              {downloading ? "Downloading…" : "Download Contract"}
            </button>
            <button
              type="button"
              onClick={startEdit}
              className="inline-flex items-center gap-1.5 border border-[#F1935C] bg-white rounded-md px-3 py-1.5 text-[12px] text-[#E07B43] font-semibold hover:bg-[#FFF7F0] cursor-pointer"
            >
              <Pencil size={13} /> Edit
            </button>
          </div>
        )}
      </div>

      <PdfDownloadPopup popup={pdfPopup} onClose={() => setPdfPopup(null)} />

      {editing && saveError && (
        <div className="flex items-center gap-2 bg-[#FEF2F2] border border-[#FECACA] rounded-[10px] px-4 py-3 text-[12px] text-[#7B3A35]">
          <AlertCircle size={15} className="text-[#C8311C] shrink-0" />
          <span>{saveError}</span>
        </div>
      )}

      {/* Contract basics */}
      <div className="bg-white border border-[#ECEEF3] rounded-[10px] px-5 py-4">
        <div className="text-[13px] font-semibold text-[#1A1B2B] mb-4">
          Contract basics
        </div>
        <div
          className="grid gap-x-6 gap-y-4"
          style={{
            gridTemplateColumns: editing
              ? "2fr 1fr 1fr 1fr"
              : "2fr 1fr 1fr 1fr 1fr",
          }}
        >
          {editing ? (
            <>
              <EditCell label="LoA Number" error={missing.loa_number}>
                <input
                  value={draft!.loa_number}
                  onChange={(e) => setBasic("loa_number", e.target.value)}
                  placeholder="E.g. CGM/PM/25-26/…"
                  className={inputClass}
                />
              </EditCell>
              <EditCell label="LoA Date" error={missing.loa_date}>
                <input
                  type="date"
                  value={draft!.loa_date}
                  onChange={(e) => setBasic("loa_date", e.target.value)}
                  className={inputClass}
                />
              </EditCell>
              <EditCell label="Supplier" error={missing.supplier}>
                <input
                  value={draft!.supplier}
                  onChange={(e) => setBasic("supplier", e.target.value)}
                  placeholder="E.g. MPPMCL"
                  className={inputClass}
                />
              </EditCell>
              <EditCell label="Receiver" error={missing.receiver}>
                <input
                  value={draft!.receiver}
                  onChange={(e) => setBasic("receiver", e.target.value)}
                  placeholder="E.g. CSPDCL"
                  className={inputClass}
                />
              </EditCell>
              <EditCell
                label="Trader Name"
                error={missing.traderName}
                className="col-span-full"
              >
                <TraderSelect
                  multiple
                  value={draft!.traderName}
                  onChange={(v) => setBasic("traderName", v)}
                  placeholder="Select traders"
                  error={missing.traderName}
                  triggerClassName={inputClass}
                />
              </EditCell>
            </>
          ) : (
            <>
              <FieldCell label="LoA Number" value={fmt(contract.loa_number)} />
              <FieldCell label="LoA Date" value={fmtDate(contract.loa_date)} />
              <FieldCell label="Supplier" value={fmt(contract.supplier)} />
              <FieldCell label="Receiver" value={fmt(contract.receiver)} />
              <TraderChipsCell
                label="Trader Name"
                value={String(contract.traderName ?? contract.trader ?? "")}
              />
            </>
          )}
        </div>
        {editing ? (
          <div className="mt-4 border-t border-[#ECEEF3] pt-4">
            <EditCell label="Notes">
              <textarea
                value={draft!.notes}
                onChange={(e) => setBasic("notes", e.target.value)}
                placeholder="Special conditions…"
                rows={3}
                className="w-full border border-[#E2E2E5] bg-white rounded-md text-[12px] px-2 py-1.5 outline-none text-[#1A1B2B] placeholder:text-[#A8A6B2] focus:border-[#F1935C] resize-y"
              />
            </EditCell>
          </div>
        ) : (
          contract.notes && (
            <div className="mt-4 border-t border-[#ECEEF3] pt-4">
              <span className="text-[11px] text-[#8C8A96] block mb-1">
                Notes
              </span>
              <span className="text-[12px] text-[#1A1B2B]">
                {contract.notes}
              </span>
            </div>
          )
        )}
      </div>

      {/* Supply Periods */}
      <div className="bg-white border border-[#ECEEF3] rounded-[10px] px-5 py-4">
        <div className="flex items-center text-[13px] font-semibold text-[#1A1B2B] mb-3">
          Supply Periods
          <Badge count={contract.supply_periods.length} />
        </div>
        {editing ? (
          draft!.supply.length === 0 ? (
            <div className="text-[12px] text-[#8C8A96]">
              No supply periods defined.
            </div>
          ) : (
            draft!.supply.map((p, i) => (
              <EditSupplyRow
                key={i}
                period={p}
                onChange={(key, value) => updateSupply(i, key, value)}
              />
            ))
          )
        ) : contract.supply_periods.length === 0 ? (
          <div className="text-[12px] text-[#8C8A96]">
            No supply periods defined.
          </div>
        ) : (
          contract.supply_periods.map((p, i) => (
            <PeriodRow key={p.id ?? i} period={p} isReturn={false} />
          ))
        )}
      </div>

      {/* Return Periods */}
      <div className="bg-white border border-[#ECEEF3] rounded-[10px] px-5 py-4">
        <div className="flex items-center text-[13px] font-semibold text-[#1A1B2B] mb-3">
          Return Period
          <Badge count={contract.return_periods.length} />
        </div>
        {editing ? (
          draft!.return.length === 0 ? (
            <div className="text-[12px] text-[#8C8A96]">
              No return periods defined.
            </div>
          ) : (
            draft!.return.map((p, i) => (
              <EditReturnRow
                key={i}
                period={p}
                onChange={(key, value) => updateReturn(i, key, value)}
              />
            ))
          )
        ) : contract.return_periods.length === 0 ? (
          <div className="text-[12px] text-[#8C8A96]">
            No return periods defined.
          </div>
        ) : (
          contract.return_periods.map((p, i) => (
            <PeriodRow key={p.id ?? i} period={p} isReturn={true} />
          ))
        )}
      </div>
    </div>
  );
}

export default DocumentsTab;
